// Messages exchanged between the service worker and its window clients, plus
// the push-payload shape. The push payload {type, callId} is the documented
// wire contract (docs/06-clients.md §4: payloads carry no names or keys);
// @merkury/protocol does not yet export a schema for it, so it is parsed
// defensively here — flagged to the protocol owners (see J10 report).

export const SKIP_WAITING = 'merkury:skip-waiting';
export const PENDING_INVITE = 'merkury:pending-invite';
/** SW → window: the user chose Decline on the notification; the page (which
 *  holds a live authenticated socket) must decline via the engine — the web
 *  twin of Android's NativeDecline delegating to a resumed listener. */
export const PENDING_DECLINE = 'merkury:pending-decline';
export const PUSH_ROTATE = 'merkury:push-rotate';

/** A decision already made on a native/notification surface. `accept` skips the
 *  in-app ring ask; `decline` drives engine.decline() the moment it can. */
export type NativeCallAction = 'accept' | 'decline' | null;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

/**
 * Push payload: `{type, callId}` — nothing else (mirrors
 * @merkury/protocol PushPayload). `incoming_call` rings; `missed_call` ends the
 * ring as a missed call; `call_cancelled` ends the ring with no missed call
 * (answered/declined on another of the user's devices).
 */
export interface PushPayload {
  type: 'incoming_call' | 'missed_call' | 'call_cancelled';
  callId: string;
  /** Server send time, epoch millis (freshness guard). Absent from an older
   *  server — a payload without it is never treated as stale. */
  sentAt?: number;
}

export function parsePushPayload(data: unknown): PushPayload | null {
  if (typeof data !== 'object' || data === null) return null;
  const { type, callId, sentAt } = data as {
    type?: unknown;
    callId?: unknown;
    sentAt?: unknown;
  };
  if (type !== 'incoming_call' && type !== 'missed_call' && type !== 'call_cancelled') return null;
  if (!isUuid(callId)) return null;
  const payload: PushPayload = { type, callId };
  // A number is the wire form (webpush/ntfy JSON). Ignore anything else — a
  // malformed stamp must never make a real invite look stale.
  if (typeof sentAt === 'number' && Number.isFinite(sentAt) && sentAt >= 0) {
    payload.sentAt = sentAt;
  }
  return payload;
}

/**
 * SW → window: an incoming-call notification was acted on. `action` carries the
 * user's decision from a notification button when one was pressed: `accept`
 * (auto-accept without a second ask) or, on a plain tap, null (the in-app ring
 * asks). Decline uses its own message (PENDING_DECLINE) so a page that only
 * understands invites never mistakes a decline for a ring.
 */
export interface PendingInviteMessage {
  type: typeof PENDING_INVITE;
  callId: string;
  action: 'accept' | null;
}

export function parsePendingInviteMessage(data: unknown): PendingInviteMessage | null {
  if (typeof data !== 'object' || data === null) return null;
  const { type, callId, action } = data as { type?: unknown; callId?: unknown; action?: unknown };
  if (type !== PENDING_INVITE || !isUuid(callId)) return null;
  return { type: PENDING_INVITE, callId, action: action === 'accept' ? 'accept' : null };
}

/** SW → window: the user pressed Decline; the page declines via the engine. */
export interface PendingDeclineMessage {
  type: typeof PENDING_DECLINE;
  callId: string;
}

export function parsePendingDeclineMessage(data: unknown): PendingDeclineMessage | null {
  if (typeof data !== 'object' || data === null) return null;
  const { type, callId } = data as { type?: unknown; callId?: unknown };
  if (type !== PENDING_DECLINE || !isUuid(callId)) return null;
  return { type: PENDING_DECLINE, callId };
}

/** Deep-link path an incoming-call notification opens when no tab exists.
 * The callId rides in the #fragment: it never reaches the network/CDN logs
 * (same rationale as the /add#<uuid> link, docs/06 §2). */
export const INVITE_FRAGMENT_PREFIX = '#invite=';

export function inviteDeepLink(callId: string): string {
  return `/${INVITE_FRAGMENT_PREFIX}${callId}`;
}

/**
 * Deep link that also carries the user's notification decision, opened when no
 * window exists to hand the decision to: `#invite=<callId>&action=accept|decline`.
 * The action rides in the fragment for the same reason the callId does — it
 * never reaches the network/CDN logs. `null` degrades to the bare invite link.
 */
export function inviteActionDeepLink(callId: string, action: NativeCallAction): string {
  if (action === null) return inviteDeepLink(callId);
  return `${inviteDeepLink(callId)}&action=${action}`;
}

/** Extract a callId from a location hash; null when absent or malformed. */
export function parseInviteFragment(hash: string): string | null {
  if (!hash.startsWith(INVITE_FRAGMENT_PREFIX)) return null;
  const callId = hash.slice(INVITE_FRAGMENT_PREFIX.length);
  return isUuid(callId) ? callId : null;
}

/** Outgoing-dial deep link placed by the Android Wear bridge when a call is
 *  started from the watch (`#dial=<peerId>`). Kept in sync with DeepLink.java
 *  `DIAL_FRAGMENT_PREFIX` / `dialHash`; the peerId is a friend's UUID. */
export const DIAL_FRAGMENT_PREFIX = '#dial=';

/** Extract a friend peerId from a `#dial=<peerId>` hash; null when absent/bad. */
export function parseDialFragment(hash: string): string | null {
  if (!hash.startsWith(DIAL_FRAGMENT_PREFIX)) return null;
  const peerId = hash.slice(DIAL_FRAGMENT_PREFIX.length);
  return isUuid(peerId) ? peerId : null;
}
